import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Mail, Paperclip, Send, ArrowLeft, X, CheckCircle, AlertCircle, FileText, Loader2, Users, Image as ImageIcon, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const BulkEmailSystem = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailData, setEmailData] = useState({
    recipientEmails: '',
    subject: '',
    message: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [emailTags, setEmailTags] = useState([]);
  const [emailSent, setEmailSent] = useState(false);
  const [emailStats, setEmailStats] = useState(null);

  // Check authentication and fetch marketing templates
  useEffect(() => {
    const token = localStorage.getItem('customerAdminToken');
    if (!token) {
      navigate('/customer-admin/login');
      return;
    }
    fetchTemplates();
  }, [navigate]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/customer-admin/marketing-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load marketing templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailInput = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const email = e.target.value.trim();
      if (email && email.includes('@')) {
        if (!emailTags.includes(email)) {
          setEmailTags([...emailTags, email]);
          setEmailData(prev => ({
            ...prev,
            recipientEmails: ''
          }));
        }
      } else if (email) {
        toast.error('Please enter a valid email address');
      }
    }
  };

  const removeEmailTag = (emailToRemove) => {
    setEmailTags(emailTags.filter(email => email !== emailToRemove));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    
    if (templateId === 'custom') {
      setEmailData(prev => ({
        ...prev,
        subject: '',
        message: ''
      }));
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEmailData(prev => ({
        ...prev,
        subject: template.subject,
        message: '' // Templates have built-in content
      }));
      toast.success(`Template "${template.name}" selected`);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
    toast.success(`${files.length} file(s) attached`);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    toast.info('Attachment removed');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const isImage = (file) => {
    return file.type.startsWith('image/');
  };

  const handleSendBulkEmail = async (e) => {
    e.preventDefault();

    // Validation
    if (emailTags.length === 0) {
      toast.error('Please add at least one recipient email address');
      return;
    }

    if (!emailData.subject) {
      toast.error('Please enter email subject');
      return;
    }

    if (!emailData.message && (!selectedTemplate || selectedTemplate === 'custom')) {
      toast.error('Please enter email message');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('customerAdminToken');
      
      // Create FormData
      const formData = new FormData();
      formData.append('recipient_emails', emailTags.join(','));
      formData.append('subject', emailData.subject);
      formData.append('message', emailData.message || 'Marketing campaign email');
      
      if (selectedTemplate && selectedTemplate !== 'custom') {
        formData.append('template_id', selectedTemplate);
      }
      
      // Append attachments
      attachments.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/customer-admin/send-bulk-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      // Handle token expiry
      if (response.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('customerAdminToken');
        navigate('/customer-admin/login');
        return;
      }

      if (!response.ok) {
        let errorMessage = 'Failed to send bulk email';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      setEmailSent(true);
      setEmailStats(result.statistics);
      
      // Show appropriate message based on results
      if (result.statistics.sent > 0 && result.statistics.failed === 0) {
        toast.success(`✅ Bulk email sent to ${result.statistics.sent} recipients!`);
      } else if (result.statistics.sent > 0 && result.statistics.failed > 0) {
        toast.warning(`⚠️ Sent to ${result.statistics.sent}, Failed: ${result.statistics.failed}`);
      } else if (result.statistics.failed > 0) {
        // Show error details if available
        const failedEmails = result.statistics.failed_emails || [];
        if (failedEmails.length > 0 && failedEmails[0].error) {
          toast.error(`❌ Failed: ${failedEmails[0].error}`);
        } else {
          toast.error(`❌ Failed to send to ${result.statistics.failed} recipients`);
        }
      }
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setEmailData({
          recipientEmails: '',
          subject: '',
          message: ''
        });
        setEmailTags([]);
        setAttachments([]);
        setSelectedTemplate('');
        setEmailSent(false);
        setEmailStats(null);
      }, 5000);

    } catch (error) {
      console.error('Error sending bulk email:', error);
      toast.error(error.message || 'Failed to send bulk email');
    } finally {
      setLoading(false);
    }
  };

  const getTemplateBadgeColor = (category) => {
    const colors = {
      'services': 'bg-blue-100 text-blue-800',
      'offers': 'bg-pink-100 text-pink-800',
      'custom': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.custom;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/customer-admin/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                Advanced Bulk Email System
              </h1>
              <p className="text-gray-600 mt-1">Send marketing emails and notifications to multiple customers from notification@netixfibernet.com</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {emailSent && emailStats && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-green-800">Bulk Email Campaign Completed!</h3>
                <p className="text-green-700 text-sm">Your email has been sent to {emailStats.sent} recipients</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div className="bg-white p-3 rounded-lg">
                <p className="text-sm text-gray-600">Total Recipients</p>
                <p className="text-2xl font-bold text-gray-900">{emailStats.total_recipients}</p>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-sm text-gray-600">Sent Successfully</p>
                <p className="text-2xl font-bold text-green-600">{emailStats.sent}</p>
              </div>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">{emailStats.failed}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Form */}
        <Card className="shadow-xl">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Bulk Email Composer
            </CardTitle>
            <CardDescription className="text-gray-100">
              From: notification@netixfibernet.com
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSendBulkEmail} className="space-y-6">
              {/* Marketing Template Selection */}
              <div>
                <Label htmlFor="template" className="text-base font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-600" />
                  Marketing Template
                </Label>
                <Select 
                  value={selectedTemplate} 
                  onValueChange={handleTemplateSelect}
                  disabled={loadingTemplates}
                >
                  <SelectTrigger className="mt-2 text-base">
                    <SelectValue placeholder={loadingTemplates ? "Loading templates..." : "Select a marketing template"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Custom Email (No Template)</SelectItem>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <span>{template.name}</span>
                          <Badge className={`text-xs ${getTemplateBadgeColor(template.category)}`}>
                            {template.category}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 mt-1">
                  Choose a professional marketing template with images or create custom email
                </p>
              </div>

              {/* Recipient Emails */}
              <div>
                <Label htmlFor="recipientEmails" className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  Customer Email Addresses
                </Label>
                <Input
                  id="recipientEmails"
                  name="recipientEmails"
                  type="email"
                  value={emailData.recipientEmails}
                  onChange={handleInputChange}
                  onKeyDown={handleEmailInput}
                  placeholder="Type email and press Enter or comma to add..."
                  className="mt-2 text-base"
                />
                <p className="text-sm text-gray-500 mt-1">Add multiple recipients (press Enter or comma after each email)</p>
                
                {/* Email Tags */}
                {emailTags.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {emailTags.map((email, index) => (
                      <Badge 
                        key={index}
                        className="bg-purple-100 text-purple-800 px-3 py-1 flex items-center gap-2"
                      >
                        <Mail className="w-3 h-3" />
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEmailTag(email)}
                          className="ml-1 hover:text-purple-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {emailTags.length > 0 && (
                  <p className="text-sm text-purple-600 font-medium mt-2">
                    Total Recipients: {emailTags.length}
                  </p>
                )}
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject" className="text-base font-semibold">
                  Email Subject
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={emailData.subject}
                  onChange={handleInputChange}
                  placeholder="Enter email subject"
                  className="mt-2 text-base"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  {selectedTemplate && selectedTemplate !== 'custom' 
                    ? 'Subject loaded from template (you can customize it)' 
                    : 'Enter a compelling subject line for your email'}
                </p>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-base font-semibold">
                  Email Content {selectedTemplate !== 'custom' && selectedTemplate && '(Optional)'}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={emailData.message}
                  onChange={handleInputChange}
                  placeholder={selectedTemplate !== 'custom' && selectedTemplate 
                    ? "Add custom description or leave empty to use template content..." 
                    : "Type your message here..."}
                  rows={12}
                  className="mt-2 text-base resize-none"
                  required={selectedTemplate === 'custom' || !selectedTemplate}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {selectedTemplate && selectedTemplate !== 'custom'
                    ? 'Template includes professional design with images. Your message will be inserted into the template.'
                    : 'Write your marketing message or announcement'}
                </p>
              </div>

              {/* File/Image Attachments */}
              <div>
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-purple-600" />
                  Attachments (Files & Images)
                </Label>
                
                <div className="mt-2">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('file-upload').click()}
                      className="flex items-center gap-2"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Add Files/Images
                    </Button>
                  </div>
                </div>

                {attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Attached Files ({attachments.length}):
                    </p>
                    {attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {isImage(file) ? (
                            <ImageIcon className="w-4 h-4 text-purple-500" />
                          ) : (
                            <Paperclip className="w-4 h-4 text-gray-500" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                              {file.name}
                              {isImage(file) && (
                                <Badge className="bg-purple-100 text-purple-800 text-xs">Image</Badge>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-purple-800">
                    <p className="font-semibold mb-1">Bulk Email Features:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Sent from <strong>notification@netixfibernet.com</strong></li>
                      <li>Professional marketing templates with embedded images</li>
                      <li>Support for multiple recipients (bulk sending)</li>
                      <li>File and image attachments supported</li>
                      <li>Campaign statistics and tracking</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/customer-admin/dashboard')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || emailTags.length === 0}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending to {emailTags.length} recipients...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Bulk Email to {emailTags.length} Recipient{emailTags.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Available Templates Preview */}
        {!loadingTemplates && templates.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Available Marketing Templates ({templates.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <div 
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-purple-500 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{template.name}</h4>
                    <Badge className={`text-xs ${getTemplateBadgeColor(template.category)}`}>
                      {template.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
                  {template.has_custom_html && (
                    <div className="flex items-center gap-1 text-xs text-purple-600">
                      <ImageIcon className="w-3 h-3" />
                      <span>Includes professional design with images</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkEmailSystem;
